import type { RangePickerProps } from 'antd/es/date-picker'
import dayjs, { Dayjs } from 'dayjs'
import type {
  ReconciliationApiItem,
  DurationDiscrepancyApi,
  ReconciliationRow,
  ReconciliationRowTime
} from '../../Reports/types/reconciliation'
import { ReconciliationPayType } from '../../Reports/types/reconciliation'

export const RECONCILIATION_DURATION_DISCREPANCY_UI_TO_API: Record<
  string,
  DurationDiscrepancyApi
> = {
  lt5: 'LESS_THAN_5_MINUTES',
  '5to15': 'BETWEEN_5_AND_15_MINUTES',
  '15to30': 'BETWEEN_15_AND_30_MINUTES',
  gt30: 'MORE_THAN_30_MINUTES'
}

export const RECONCILIATION_MAX_PAST_MONTHS = 12
export const RECONCILIATION_MAX_RANGE_MONTHS = 6

export const getReconciliationRangeDisabledDate: NonNullable<
  RangePickerProps['disabledDate']
> = (current, info) => {
  const today = dayjs().startOf('day')
  const c = current.startOf('day')

  if (c.isAfter(today, 'day')) return true

  const earliest = today
    .subtract(RECONCILIATION_MAX_PAST_MONTHS, 'month')
    .startOf('day')
  if (c.isBefore(earliest, 'day')) return true

  const from = info?.from
  if (!from) return false

  const f = from.startOf('day')
  const windowStart = f.subtract(RECONCILIATION_MAX_RANGE_MONTHS, 'month')
  const windowEnd = f.add(RECONCILIATION_MAX_RANGE_MONTHS, 'month')

  if (c.isBefore(windowStart, 'day') || c.isAfter(windowEnd, 'day')) return true

  return false
}

export function isValidReconciliationDateRange(
  start: Dayjs,
  end: Dayjs
): boolean {
  const s = start.startOf('day')
  const e = end.startOf('day')
  const today = dayjs().startOf('day')
  if (!e.isAfter(s, 'day')) return false
  const earliestStart = today
    .subtract(RECONCILIATION_MAX_PAST_MONTHS, 'month')
    .startOf('day')
  if (s.isBefore(earliestStart, 'day')) return false
  if (e.isAfter(today, 'day')) return false
  const maxEnd = s
    .add(RECONCILIATION_MAX_RANGE_MONTHS, 'month')
    .startOf('day')
  if (e.isAfter(maxEnd, 'day')) return false
  return true
}

export function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m}m`
  if (m === 0) return `${h}h`
  return `${h}h ${m}m`
}

export function formatTime(isoString: string): string {
  return dayjs(isoString).format('HH:mm')
}

export function formatTimeRange(rowTime: ReconciliationRowTime): string {
  return `${dayjs(rowTime.startTime).format('HH:mm')}–${dayjs(rowTime.endTime).format('HH:mm')} · ${formatMinutes(rowTime.durationMinutes)}`
}

export function formatDisplayName(
  name: string | { first?: string; last?: string } | null | undefined
): string {
  if (name == null) return ''
  if (typeof name === 'string') return name
  const first = name.first ?? ''
  const last = name.last ?? ''
  return `${first} ${last}`.trim()
}

export function rowKey(r: ReconciliationRow): string {
  if (r.rotaInstanceId) {
    return `${r.rotaInstanceId}:${r.teamMemberId}`
  }
  return `${r.date}:${r.startTimeIso}:${r.teamMemberId}`
}

export function mapReconciliationApiItemToRow(
  item: ReconciliationApiItem
): ReconciliationRow {
  const plannedTime: ReconciliationRowTime = {
    startTime: item.startTime,
    endTime: item.endTime,
    durationMinutes: item.plannedDuration
  }

  let actualTime: ReconciliationRowTime | null = null
  const ci = item.clockIn
  if (ci?.clockInTime) {
    const endIso = ci.clockOutTime ?? item.endTime
    const durationMinutes =
      ci.clockInDuration ??
      Math.max(0, dayjs(endIso).diff(dayjs(ci.clockInTime), 'minute'))
    actualTime = {
      startTime: ci.clockInTime,
      endTime: endIso,
      durationMinutes
    }
  }

  const clockInDuration = item.clockIn?.clockInDuration
  const durationDiscrepancy =
    item.plannedDuration != null && clockInDuration != null
      ? clockInDuration - item.plannedDuration
      : null

  return {
    rotaInstanceId: item._id,
    startTimeIso: item.startTime,
    teamMemberId: item.teamMember.id,
    status: item.status,
    date: dayjs(item.startTime).format('YYYY-MM-DD'),
    teamMember: {
      id: item.teamMember.id,
      name: formatDisplayName(item.teamMember.name),
      avatarUrl: item.teamMember.picture || null
    },
    serviceUser: item.subject
      ? {
          id: item.subject.id,
          name: item.subject.name,
          avatarUrl: item.subject.picture || null
        }
      : null,
    eventType: {
      id: item.instanceType.id,
      name: item.instanceType.name
    },
    plannedTime,
    actualTime,
    isCanceled: false,
    durationDiscrepancy,
    warningCount: item.warnings,
    warnings: [],
    payRate: null,
    hasShiftRate: item.siteShift,
    reconciliation: null
  }
}

export function combineDateAndTime(
  date: Dayjs | null,
  time: Dayjs | null
): Dayjs | null {
  if (!date || !time) return null
  return date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0)
}

export function calcPayAmount(ratePerHour: number, minutes: number): string {
  return (Math.round(ratePerHour * (minutes / 60) * 100) / 100).toFixed(2)
}

export function getPayPreview(
  payType: ReconciliationPayType,
  row: ReconciliationRow,
  combinedStart: Dayjs | null,
  combinedEnd: Dayjs | null
): { amount: string; minutes: number } | null {
  if (payType === ReconciliationPayType.NONE)
    return { amount: '0.00', minutes: 0 }

  const rate = row.payRate
  if (!rate) return null

  if (payType === ReconciliationPayType.SHIFT_RATE) {
    return { amount: rate.amount, minutes: 0 }
  }

  let minutes = 0
  if (payType === ReconciliationPayType.PLANNED) {
    minutes = row.plannedTime.durationMinutes
  } else if (payType === ReconciliationPayType.ACTUAL && row.actualTime) {
    minutes = row.actualTime.durationMinutes
  } else if (
    payType === ReconciliationPayType.CUSTOM &&
    combinedStart &&
    combinedEnd
  ) {
    minutes = combinedEnd.diff(combinedStart, 'minute')
  }
  if (minutes <= 0) return null

  const amount =
    rate.unit === 'shift'
      ? rate.amount
      : calcPayAmount(parseFloat(rate.amount), minutes)
  return { amount, minutes }
}

